import {
  Box,
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronRight,
  Clock,
  Folder,
  Link as LinkIcon,
  Settings,
  Sparkles,
  User,
} from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HighlightedText } from '../../../components/HighlightedText.js';
import { Badge } from '../../../components/ui/badge.js';
import { Button } from '../../../components/ui/button.js';
import { TableCell, TableRow } from '../../../components/ui/table.js';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip.js';
import { cn } from '../../../lib/utils.js';
import type { CustomFieldsItem, PlanningObjectItem, WorkfrontObjectItem } from '../hooks.js';
import type { ScenarioRow as ScenarioRowData } from '../types.js';
import { isActive } from '../types.js';
import { formatDateTime, formatDuration, formatSchedule } from '../utils.js';
import { ApplicationIcon } from './ApplicationIcon.js';
import { ScenarioActionsCell } from './ScenarioActionsCell.js';
import { ScenarioAiSummarySheet } from './ScenarioAiSummarySheet.js';
import { ScenarioExpandPanel } from './ScenarioExpandPanel.js';
import { ScenarioKpiSelector } from './ScenarioKpiSelector.js';
import { TagChip } from './TagChip.js';

/**
 * A single rich scenario row. Mirrors the row markup from
 * `wf-audit/src/slices/scenario/components/scenario/DefaultTable.tsx`
 * (lines ~1094–1302): name link + sub-line `Modules / Folder / KPIs`
 * + status badge, stacked-circle application icons (first 5 + `+N`
 * overflow), description, run-metrics block, modified-at + by.
 *
 * The expand chevron + sub-row appears when `showExpand` is true
 * (Administrator + Developer routes) AND the row has detected
 * workfront-object rows or custom-field annotations. Data for the
 * sub-row comes from license-wide `/v1/scenarios/workfront-objects-data`
 * and `/v1/scenarios/custom-fields-data` fetches grouped by
 * `fusionScenarioId`/`fusionId` upstream.
 */
export interface ScenarioRowProps {
  row: ScenarioRowData;
  index: number;
  showExpand: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  workfrontObjects: ReadonlyArray<WorkfrontObjectItem>;
  customFields: CustomFieldsItem | undefined;
  planningObjects: ReadonlyArray<PlanningObjectItem>;
  expandedColSpan: number;
  /** Render the trailing actions cell (comment / wrench / dropdown). */
  showActions: boolean;
  /** Render the wrench-dropdown inside the actions cell. */
  showActionsDropdown: boolean;
  /** Current free-text search term — used to highlight matches in the
   *  scenario name. Empty string is the no-op default. */
  searchQuery?: string;
  /** Called when the operator clicks the per-Workfront-object search
   *  button inside the expand panel. Sets the page-level search input
   *  to the clicked object's ID. Optional — when omitted the button
   *  is hidden. */
  onSearch?: (query: string) => void;
  /** Marks the row as the keyboard-cursor target. Renders a brand-
   *  yellow left edge + subtle background so the operator sees where
   *  ↑/↓ navigation has landed. */
  keyboardActive?: boolean;
}

export function ScenarioRow({
  row,
  index,
  showExpand,
  expanded,
  onToggleExpand,
  workfrontObjects,
  customFields,
  planningObjects,
  expandedColSpan,
  showActions,
  showActionsDropdown,
  searchQuery = '',
  onSearch,
  keyboardActive = false,
}: ScenarioRowProps): JSX.Element {
  const apps = row.applications ?? [];
  const visibleApps = apps.slice(0, 5);
  const overflow = apps.length > 5 ? apps.length - 5 : 0;
  // Local state for the per-row business-summary sheet — the row
  // already owns its action sheets (FusionRequest, Tech Summary) via
  // ScenarioActionsCell, so adding one more in-row sheet keeps the
  // component boundaries consistent.
  const [businessSummaryOpen, setBusinessSummaryOpen] = useState(false);
  const hasCustomFields =
    customFields !== undefined &&
    Array.isArray(customFields.customFieldsData) &&
    (customFields.customFieldsData as ReadonlyArray<unknown>).length > 0;
  const hasExpandable =
    workfrontObjects.length > 0 || hasCustomFields || planningObjects.length > 0;
  const showExpandButton = showExpand && hasExpandable;

  return (
    <>
      <TableRow
        className={
          'border-foreground/10 align-top hover:bg-foreground/[0.04] ' +
          (keyboardActive
            ? 'bg-foreground/[0.06] border-l-2 border-l-[color:var(--brand-yellow)]'
            : '')
        }
        aria-current={keyboardActive ? 'true' : undefined}
      >
        {showExpand ? (
          <TableCell className="w-10 p-2">
            {showExpandButton ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onToggleExpand}
                className="h-7 w-7 text-foreground/60 hover:bg-foreground/10 hover:text-foreground"
                aria-label={expanded ? 'Collapse row' : 'Expand row'}
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            ) : null}
          </TableCell>
        ) : null}

        <TableCell className="w-10 p-2 text-center text-sm text-foreground/60">{index}</TableCell>

        <TableCell className="min-w-[420px] p-2">
          <div className="flex flex-col gap-1">
            <ScenarioNameButton row={row} searchQuery={searchQuery} />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground/60">
              <span className="inline-flex items-center gap-1">
                <Box className="h-3.5 w-3.5" /> Modules: {row.totalModules ?? 0}
              </span>
              <span className="inline-flex items-center gap-1">
                <Folder className="h-3.5 w-3.5" /> Folder: {row.folderName || '—'}
              </span>
              <ScenarioKpiSelector
                fusionScenarioId={row.fusionId}
                initialCount={row.kpiCount ?? 0}
              />
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {isActive(row) ? (
                <Badge className="border-0 bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20">
                  Active
                </Badge>
              ) : (
                <Badge className="border-0 bg-rose-500/20 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20">
                  Inactive
                </Badge>
              )}
              {isStaleRun(row) ? (
                <Badge
                  className="border-0 bg-amber-500/20 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20"
                  title={
                    row.lastSuccessfulRunAt
                      ? `Last successful run: ${formatDateTime(row.lastSuccessfulRunAt)}`
                      : 'No successful run on record'
                  }
                >
                  Stale
                </Badge>
              ) : null}
              {(row.tags ?? []).map((t) => (
                <TagChip key={t.id} tag={t} size="sm" />
              ))}
              {row.url ? <FusionScenarioLink url={row.url} name={row.name} /> : null}
            </div>
          </div>
        </TableCell>

        <TableCell className="p-2">
          <div className="flex items-center -space-x-1.5">
            {visibleApps.map((app, i) => (
              <ApplicationIcon key={i} app={app} size={28} sizeIcon={20} />
            ))}
            {overflow > 0 ? (
              <div
                className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-gray-500 bg-gray-800 text-xs font-medium text-foreground"
                title={`${overflow} more`}
              >
                +{overflow}
              </div>
            ) : null}
          </div>
        </TableCell>

        <TableCell className="min-w-[280px] p-2">
          <div className="flex items-start gap-2">
            <span className="line-clamp-3 flex-1 text-sm text-foreground/80">
              {scenarioDescriptionPreview(row) || '—'}
            </span>
            {row.aiBusinessSummary ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setBusinessSummaryOpen(true)}
                      className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-foreground/50 hover:bg-foreground/10 hover:text-foreground/80"
                      aria-label="Show business summary"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Show Business Summary</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
          </div>
        </TableCell>

        <TableCell className="min-w-[220px] p-2">
          <RunMetricsBlock row={row} />
        </TableCell>

        <TableCell className="min-w-[180px] p-2 text-sm text-foreground/80">
          {row.modifiedAt ? (
            <>
              <div>{formatDateTime(row.modifiedAt)}</div>
              {row.modifiedBy ? (
                <div className="mt-1 inline-flex items-center gap-1 text-xs text-foreground/60">
                  <User className="h-3.5 w-3.5" /> by {row.modifiedBy}
                </div>
              ) : null}
            </>
          ) : (
            '—'
          )}
        </TableCell>

        {showActions ? (
          // Pinned to the right of the scroll container so the dropdown
          // (and Comment + AI summary icons) stay reachable regardless of
          // viewport width. Solid `--gradient-end` background masks
          // scrolled-under cells; the 1px inner-left shadow visually
          // separates the pinned column from the scrolling area. Hover +
          // keyboard-active row tints don't paint through (parent row
          // `hover:bg-foreground/[0.04]` is blocked by the cell's own bg) —
          // acceptable trade for keeping the action icons in view.
          //
          // Width matches the header column (admin: w-20, dev: w-32).
          <TableCell
            className={cn(
              'sticky right-0 bg-[color:var(--surface-overlay)] p-2 shadow-[inset_1px_0_0_rgba(255,255,255,0.06)]',
              showActionsDropdown ? 'w-32' : 'w-20',
            )}
          >
            <ScenarioActionsCell row={row} showActionsDropdown={showActionsDropdown} />
          </TableCell>
        ) : null}
      </TableRow>

      {row.aiBusinessSummary ? (
        <ScenarioAiSummarySheet
          open={businessSummaryOpen}
          onOpenChange={setBusinessSummaryOpen}
          title={`Business Summary for "${row.name ?? row.fusionId}"`}
          text={row.aiBusinessSummary}
        />
      ) : null}

      {expanded && showExpandButton ? (
        <TableRow className="border-foreground/10 bg-foreground/[0.02] hover:bg-foreground/[0.02]">
          <TableCell colSpan={expandedColSpan} className="p-4">
            {/* sticky + max-width wrapper: the parent table is wider
               than the viewport when many columns are visible, so the
               expand panel would otherwise stretch edge-to-edge and
               scroll horizontally with the table. Sticking it to the
               left of the scroll container + clamping to
               max-w-screen-xl keeps it within the visible viewport
               regardless of horizontal scroll position. */}
            <div className="sticky left-4 w-fit max-w-screen-xl">
              <ScenarioExpandPanel
                scenarioUrl={row.url}
                workfrontObjects={workfrontObjects}
                customFields={customFields}
                planningObjects={planningObjects}
                onSearch={onSearch}
              />
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}

function RunMetricsBlock({ row }: { row: ScenarioRowData }): JSX.Element {
  const schedule = formatSchedule(row.eventSchedule ?? undefined);
  const opsRaw = row.lastSuccessfulRunOperations;
  const ops = opsRaw && opsRaw !== '0' ? opsRaw : null;
  const durRaw = row.lastSuccessfulRunDuration;
  const dur = durRaw && durRaw !== '0' ? formatDuration(Number(durRaw)) : '';
  const lastRun = row.lastSuccessfulRunAt ? formatDateTime(row.lastSuccessfulRunAt) : '';

  if (!lastRun && !ops && !dur && !schedule) {
    return <span className="text-sm text-foreground/40">—</span>;
  }
  return (
    <div className="space-y-1 text-sm text-foreground/80">
      {lastRun ? <div>{lastRun}</div> : null}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-foreground/60">
        {ops !== null ? (
          <span className="inline-flex items-center gap-1">
            <Settings className="h-3.5 w-3.5" /> Operations: {ops}
          </span>
        ) : null}
        {dur ? (
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> Duration: {dur}
          </span>
        ) : null}
        {schedule ? (
          <span className="inline-flex items-center gap-1">
            <CalendarIcon className="h-3.5 w-3.5" /> Schedule: {schedule}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Scenario-name click target. Mirrors the legacy behaviour of writing
 * `?scenarioId=...` into the URL instead of navigating to a separate
 * page — the listing stays mounted underneath so closing the modal
 * snaps the operator back to the same scroll position + pagination
 * state.
 */
function ScenarioNameButton({
  row,
  searchQuery,
}: {
  row: ScenarioRowData;
  searchQuery: string;
}): JSX.Element {
  const [params, setParams] = useSearchParams();
  const name = row.name || `Scenario ${row.fusionId}`;
  return (
    <button
      type="button"
      onClick={() => {
        const next = new URLSearchParams(params);
        next.set('scenarioId', row.fusionId);
        setParams(next);
      }}
      className="text-left text-base font-medium text-foreground hover:underline"
    >
      <HighlightedText text={name} query={searchQuery} />
    </button>
  );
}

/**
 * Inline chain-link button rendered at the end of the badges row when
 * the row has a Fusion URL. One click opens the scenario in Fusion's
 * own editor in a new tab — the same affordance the Actions dropdown
 * exposes under "Open Scenario", but always visible so the operator
 * doesn't have to drill through the dropdown.
 *
 * Mirrors the legacy `wf-audit/src/slices/scenario/components/
 * scenario/DefaultTable.tsx` — there the chain-link icon sat to the
 * right of the tag chips on the badges row (NOT next to the scenario
 * name; pre-2026-05-23 we had it next to the name and it visually
 * competed with the title). Icon style is the chain-link glyph (one
 * link of a chain), not an outward arrow — the legacy reads as "go
 * to the linked thing" rather than "leave this page".
 *
 * Visible regardless of role (Business / Admin / Developer). The URL
 * itself is a Fusion link the operator either has access to or
 * doesn't, so role-gating here would just hide a discoverable path.
 */
function FusionScenarioLink({ url, name }: { url: string; name: string }): JSX.Element {
  const label = name ? `Open "${name}" in Fusion` : 'Open scenario in Fusion';
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-5 w-5 items-center justify-center rounded text-foreground/50 hover:bg-foreground/10 hover:text-foreground/80"
      title={label}
      aria-label={label}
    >
      <LinkIcon className="h-3.5 w-3.5" />
    </a>
  );
}

/**
 * Description-column text. Prefers the AI-generated business summary
 * (`aiBusinessSummary`) when present; falls back to the raw Fusion
 * scenario description. Mirrors the legacy where the business summary
 * was the canonical "what does this scenario do?" surface in the list
 * — the raw Fusion description is often empty, terse, or technical
 * boilerplate.
 *
 * Strips leading markdown headers (`## Executive Summary` etc.) and
 * trims to plain prose for the line-clamped cell. The full markdown
 * is still rendered in the Scenario Detail modal + the AI Summary
 * sheet (the wrench/sparkles affordance in the actions cell), where
 * we have room for proper heading + list rendering.
 */
function scenarioDescriptionPreview(row: ScenarioRowData): string {
  const raw = row.aiBusinessSummary || row.description || '';
  if (!raw) return '';
  return stripLeadingMarkdown(raw);
}

function stripLeadingMarkdown(text: string): string {
  // Drop leading `#`-style headings (one or more lines that start with
  // hashes) so the column reads as plain prose. Strip surrounding
  // whitespace on the result; the line-clamp on the cell handles the
  // visual truncation. Markdown emphasis (`**bold**`, `*italic*`) is
  // intentionally left as-is — those render as literal characters,
  // which is fine for the 3-line preview and avoids a full markdown
  // → text round-trip on every render.
  return text
    .replace(/^\s*#{1,6}\s+[^\n]*\n+/gm, '')
    .replace(/^\s+/, '')
    .trim();
}

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/**
 * "Stale" = scenario looks like it should be producing data (Active +
 * has a schedule or recent operations history) but the engine's last
 * captured successful-run timestamp is more than 24 hours old, OR
 * there's no successful-run timestamp at all for an Active scenario.
 *
 * Skips Inactive scenarios — those are deliberately paused, so an
 * old timestamp is expected and a "Stale" pill would be noise.
 */
function isStaleRun(row: ScenarioRowData): boolean {
  if (!isActive(row)) return false;
  // Inactive heuristics — scenarios with no schedule + no ops history
  // are likely brand-new / unconfigured rather than stale.
  const hasSchedule = formatSchedule(row.eventSchedule ?? undefined).length > 0;
  const opsRaw = row.lastSuccessfulRunOperations;
  const hasOpsHistory = !!opsRaw && opsRaw !== '0';
  if (!hasSchedule && !hasOpsHistory) return false;
  if (!row.lastSuccessfulRunAt) return true;
  const ts = Date.parse(row.lastSuccessfulRunAt);
  if (!Number.isFinite(ts)) return true;
  return Date.now() - ts > STALE_THRESHOLD_MS;
}
